"use client";

import React, { useState, useEffect, useMemo } from "react";
import { CatalogueImage } from "@/components/catalog/catalogue-image";
import {
  ProductFiltersDialog,
  type ProductAdvancedFilters,
} from "@/components/catalog/product-filters-dialog";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import {
  ListProductsModerationStatusEnum,
  ListProductsProcessStatusEnum,
  type ListProductsRequest,
  type Product,
  type ProductListResponse,
} from "@sakura-tech-lab/dashboard-api";
import { useAuth } from "@/context/AuthContext";
import { useDebounce } from "@/hooks/use-debounce";
import { listProducts } from "@/services/product.service";
import { CategoryService, type CategoryTreeNode } from "@/services/category.service";
import { CategoryFilterPopover } from "@/components/catalog/category-filter-popover";
import {
  expandCategoryIds,
  rollUpCounts,
  type DirectCategoryCount,
} from "@/lib/categories/counts";
import { localizedText } from "@/lib/categories/localization";
import { findNode } from "@/lib/categories/tree-model";
import { productsApi } from "@/services/dashboard-api.client";
import { ImportProgressBanner } from "@/components/catalog/import-progress-banner";
import {
  Search,
  RotateCcw,
  // Download,
  // Plus,
  ExternalLink,
  X,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination, MobilePagination } from "@/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ShimmerText } from "@/components/ui/shimmer-text";
import { IngestionStatusCell } from "@/components/catalog/IngestionStatusCell";
import {
  getIngestionProgress,
  hasEnrichedTitle,
  isIngestionActive,
} from "@/lib/ingestion-progress";
import { highestSeverity, SEVERITY_STYLES } from "@/lib/moderation";
import { useModerationRuleLabelMap } from "@/hooks/use-moderation-rule-labels";
import { humanizeKey, resolveCategoryPaths } from "@/components/product-detail/product-detail-utils";
import { cn } from "@/lib/utils";

// Localized-field view over the generated Product's free-form name/description maps.
type MultilingualField = {
  readonly [key: string]: string | number | boolean | Record<string, unknown> | null | undefined;
};

export type { Product };

type PriceField = Product["price"];

type SortKey = "newest" | "oldest" | "updated" | "price_asc" | "price_desc";

const SORT_PARAMS: Record<SortKey, { sort: string; order: "asc" | "desc" } | null> = {
  newest: null, // server default: created_at desc
  oldest: { sort: "created_at", order: "asc" },
  updated: { sort: "updated_at", order: "desc" },
  price_asc: { sort: "price", order: "asc" },
  price_desc: { sort: "price", order: "desc" },
};

function enumValue<T extends string>(
  values: Record<string, T>,
  value: string,
): T | undefined {
  return Object.values(values).find((candidate) => candidate === value);
}


interface ProductCatalogProps {
  catalogueId?: string | null;
  catalogueName: string;
  deletedAt?: string;
  capabilities: string[];
}

export function ProductCatalog({ catalogueId, capabilities }: ProductCatalogProps) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Table state lives in the URL, so back-navigation and shared links restore
  // the exact page, filters, sort, and search.
  const urlQuery = searchParams.get("q") ?? "";
  const [searchTerm, setSearchTerm] = useState(urlQuery);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const currentPage = Math.max(1, Number.parseInt(searchParams.get("page") ?? "", 10) || 1);
  const parsedPageSize = Number.parseInt(searchParams.get("pageSize") ?? "", 10);
  const pageSize = [5, 10, 20, 50].includes(parsedPageSize) ? parsedPageSize : 10;

  // Filters and sorting (server-side; "" = filter off)
  const categoryFilter = searchParams.get("category") ?? "";
  const brandFilter = searchParams.get("brand") ?? "";
  const publishedFilter = searchParams.get("published") ?? "";
  const stockFilter = searchParams.get("stock") ?? "";
  const processStatusFilter = searchParams.get("status") ?? "";
  const moderationFilter = searchParams.get("moderation") ?? "";
  const multipleVariantsFilter = searchParams.get("multipleVariants") === "true";
  const multipleOffersFilter = searchParams.get("multipleOffers") === "true";
  const sortParam = searchParams.get("sort") ?? "newest";
  const sortKey: SortKey = sortParam in SORT_PARAMS ? (sortParam as SortKey) : "newest";
  const supportsCategories = capabilities.includes("categorization");
  const supportsModeration = capabilities.includes("moderation");
  const supportsEnrichment = capabilities.includes("enrichment");

  const advancedFilters: ProductAdvancedFilters = {
    published: publishedFilter || "all",
    stock: stockFilter || "all",
    processing: processStatusFilter || "all",
    moderation: supportsModeration ? moderationFilter || "all" : "all",
    multipleVariants: multipleVariantsFilter,
    multipleOffers: multipleOffersFilter,
  };

  // Merges updates into the current query string (preserving e.g. ?tab=) and
  // drops empty values; resetPage returns to page 1 when filters change.
  const updateParams = (
    updates: Record<string, string | null>,
    { resetPage = false }: { resetPage?: boolean } = {},
  ) => {
    const next = new URLSearchParams(searchParams);
    if (resetPage) next.delete("page");
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const activeSelectFilterCount = [
    supportsCategories ? categoryFilter : "",
    brandFilter,
    publishedFilter,
    stockFilter,
    processStatusFilter,
    supportsModeration ? moderationFilter : "",
  ].filter(Boolean).length;
  const activeFilterCount = activeSelectFilterCount
    + Number(multipleVariantsFilter)
    + Number(multipleOffersFilter);

  const applyAdvancedFilters = (filters: ProductAdvancedFilters) => {
    updateParams(
      {
        published: filters.published === "all" ? null : filters.published,
        stock: filters.stock === "all" ? null : filters.stock,
        status: filters.processing === "all" ? null : filters.processing,
        moderation: supportsModeration && filters.moderation !== "all"
          ? filters.moderation
          : null,
        multipleVariants: filters.multipleVariants ? "true" : null,
        multipleOffers: filters.multipleOffers ? "true" : null,
        structure: null,
      },
      { resetPage: true },
    );
  };

  const clearFilters = () => {
    setSearchTerm("");
    updateParams(
      {
        q: null,
        category: null,
        brand: null,
        published: null,
        stock: null,
        status: null,
        moderation: null,
        multipleVariants: null,
        multipleOffers: null,
        structure: null,
      },
      { resetPage: true },
    );
  };

  const formatLocalizedValue = (value: MultilingualField[string]): string => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return "";
  };

  const localizedEntries = (field: MultilingualField | null | undefined): Array<readonly [string, string]> => {
    return Object.entries(field ?? {}).flatMap(([language, value]) => {
      const displayValue = formatLocalizedValue(value);
      return displayValue ? [[language, displayValue] as const] : [];
    });
  };

  const productDisplayName = (product: Product): string => {
    const names = localizedEntries(product.name);
    return (
      formatLocalizedValue(product.name?.en) ||
      names[0]?.[1] ||
      product.productLabel ||
      product.caption ||
      product.sku ||
      "Unnamed product"
    );
  };

  // Prefer localized category details, with legacy category names as a fallback.
  const productCategoryPaths = (
    product: Product,
    tree: readonly CategoryTreeNode[],
  ): Array<{ leaf: string; path: string }> => {
    const assignedCategories = product.categoryDetails?.length
      ? product.categoryDetails
      : product.categories ?? [];
    return resolveCategoryPaths(assignedCategories, tree).map((category) => {
      const leaf = category.names[category.names.length - 1] ?? "Unknown category";
      return {
        leaf,
        path: category.names.join(" › "),
      };
    });
  };

  // Fetch catalogue name
  useEffect(() => {
    /*
    const fetchCatalogueName = async () => {
      if (catalogueId) {
        try {
          const catalogue = await CatalogueService.getById(catalogueId);
          setCatalogueName(catalogue.name);
        } catch (error) {
          console.error("Failed to fetch catalogue name:", error);
        }
      }
    };
    fetchCatalogueName();
    */
  }, [catalogueId]);

  // Keep the input in sync when the URL changes (back/forward, shared links).
  useEffect(() => {
    setSearchTerm((previous) => (previous === urlQuery ? previous : urlQuery));
  }, [urlQuery]);

  // Persist the debounced search term into the URL (guarded against loops).
  useEffect(() => {
    if (debouncedSearchTerm !== urlQuery) {
      updateParams({ q: debouncedSearchTerm || null }, { resetPage: true });
    }
  });

  // Category tree + per-category product counts for the filter popover.
  const { data: categoryTree } = useSWR<CategoryTreeNode[]>(
    isAuthenticated && catalogueId && supportsCategories ? ["catalog-categories", catalogueId] : null,
    () => CategoryService.getTree(catalogueId!),
    { revalidateOnFocus: false }
  );
  const { data: categoryCounts } = useSWR<DirectCategoryCount[]>(
    isAuthenticated && catalogueId && supportsCategories
      ? ["catalog-category-counts", catalogueId]
      : null,
    () => CategoryService.getProductCounts(catalogueId!),
    { revalidateOnFocus: false }
  );

  const categoryNodes = useMemo(() => categoryTree ?? [], [categoryTree]);
  const rolledUpCounts = useMemo(
    () => rollUpCounts(categoryNodes, categoryCounts ?? []),
    [categoryNodes, categoryCounts]
  );

  const selectedCategory = useMemo(
    () => (categoryFilter ? findNode(categoryNodes, categoryFilter) : null),
    [categoryNodes, categoryFilter]
  );

  // Filtering a branch means naming every category in it. Falls back to the
  // category alone when the branch is too large for the query string.
  const categoryQuery = useMemo(
    () => (categoryFilter ? expandCategoryIds(categoryNodes, categoryFilter) : null),
    [categoryNodes, categoryFilter]
  );

  const sortParams = SORT_PARAMS[sortKey];
  const productRequest: ListProductsRequest | null = catalogueId
    ? {
        catalogueId,
        page: currentPage,
        pageSize,
        search: debouncedSearchTerm || undefined,
        categoryId: supportsCategories ? categoryQuery?.ids.join(",") : undefined,
        brandId: brandFilter || undefined,
        isPublished: publishedFilter ? publishedFilter === "true" : undefined,
        inStock: stockFilter ? stockFilter === "true" : undefined,
        minVariantCount: multipleVariantsFilter ? 2 : undefined,
        minOfferCount: multipleOffersFilter ? 2 : undefined,
        processStatus: enumValue(ListProductsProcessStatusEnum, processStatusFilter),
        moderationStatus: supportsModeration
          ? enumValue(ListProductsModerationStatusEnum, moderationFilter)
          : undefined,
        sort: sortParams?.sort,
        order: sortParams?.order,
      }
    : null;

  // Fetch products with SWR - only fetch if authenticated
  const { data, error, isLoading } = useSWR<ProductListResponse>(
    isAuthenticated && productRequest
      ? (["products", productRequest] as const)
      : null,
    () => listProducts(productRequest!),
    {
      revalidateOnFocus: false,
      // Keep stage counters moving while any visible product is still ingesting.
      refreshInterval: (latestData) =>
        (latestData?.data ?? []).some((product) => isIngestionActive(product)) ? 5000 : 0,
      onSuccess: (data) => {
        setTotalPages(data.meta.totalPages);
        setTotalItems(data.meta.totalItems);
      },
    }
  );

  // Derived state - filtering/sorting is fully server-side now
  const products: Product[] = Array.isArray(data?.data) ? data.data : [];

  // Human-readable rule names for the violation indicator tooltips.
  const moderationRulesetIds = [
    ...new Set(
      products
        .map((product) => product.moderation?.rulesetId)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const { labelFor } = useModerationRuleLabelMap(supportsModeration ? moderationRulesetIds : []);

  // Highest-severity moderation violation for the status column, with rule
  // names for the tooltip (deduped — a rule can violate on multiple images).
  const violationSummary = (
    product: Product,
  ): { level: "high" | "medium" | "low"; count: number; names: string[] } | null => {
    const violations = product.moderation?.violations ?? [];
    const level = highestSeverity(violations);
    if (!level) return null;
    const names = [
      ...new Set(
        violations.map(
          (violation) =>
            labelFor(String(violation.slug ?? "")) ?? humanizeKey(String(violation.slug ?? "")),
        ),
      ),
    ];
    return { level, count: violations.length, names };
  };


  // Brand options for the filter dropdown
  const { data: brandsData } = useSWR(
    isAuthenticated && catalogueId
      ? (["product-brands", catalogueId] as const)
      : null,
    () => productsApi.listProductBrands({ catalogueId: catalogueId! }),
    { revalidateOnFocus: false }
  );
  const brandOptions = (brandsData?.data ?? []).flatMap((brand) =>
    brand.id && brand.name ? [{ id: brand.id, name: brand.name }] : []
  );

  // Handle search with debounce
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    updateParams({ page: page === 1 ? null : String(page) });
  };

  // Toggle select all products
  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((product) => product.id));
    }
  };

  // Toggle select individual product
  const toggleSelectProduct = (productId: string) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter((id) => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  // Format product ID to be shorter and cleaner
  const formatProductId = (id: string) => {
    // If ID is already in PR000X format, return as is
    if (id.startsWith("PR") && id.length <= 6) {
      return id;
    }
    // Otherwise, create a short format from the full ID
    const shortId = id.substring(0, 6).toUpperCase();
    return shortId.startsWith("PR") ? shortId : `PR${shortId.substring(0, 4)}`;
  };

  const getDisplayPrice = (price: PriceField): { amount: string; currency: string; isFallback: boolean } => {
    // Try EUR first
    if (price?.EUR) {
      return { amount: price.EUR.toFixed(2), currency: 'EUR', isFallback: false };
    }
    
    // Fall back to next available price (not EUR)
    const entries = Object.entries(price || {});
    const nonEUREntries = entries.filter(([currency]) => currency !== 'EUR');
    if (nonEUREntries.length > 0) {
      const [nextCurrency, nextPrice] = nonEUREntries[0];
      return { amount: nextPrice?.toFixed(2) || 'N/A', currency: nextCurrency, isFallback: true };
    }
    
    // No prices available
    return { amount: 'N/A', currency: 'EUR', isFallback: false };
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-1.5 shadow-sm">
        <div className="flex flex-col gap-1.5 xl:flex-row xl:items-center">
          <div className="flex h-9 min-w-[220px] flex-1 items-center gap-2 rounded-md bg-muted/50 px-2.5 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              type="search"
              aria-label="Search products"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <FilterSelect
              label="Sort"
              value={sortKey}
              onValueChange={(value) =>
                updateParams({ sort: value === "newest" ? null : value }, { resetPage: true })
              }
              triggerClassName="w-[150px]"
              options={[
                { value: "newest", label: "Newest" },
                { value: "oldest", label: "Oldest" },
                { value: "updated", label: "Recently updated" },
                { value: "price_asc", label: "Price: low to high" },
                { value: "price_desc", label: "Price: high to low" },
              ]}
            />
            {supportsCategories ? (
              <CategoryFilterPopover
                nodes={categoryNodes}
                counts={rolledUpCounts}
                selectedId={categoryFilter || null}
                onSelect={(id) => updateParams({ category: id }, { resetPage: true })}
              />
            ) : null}
            <FilterSelect
              label="Brand"
              value={brandFilter || "all"}
              onValueChange={(value) =>
                updateParams({ brand: value === "all" ? null : value }, { resetPage: true })
              }
              triggerClassName="w-[140px]"
              options={[
                { value: "all", label: "All brands" },
                ...brandOptions.map((brand) => ({ value: brand.id, label: brand.name })),
              ]}
            />

            <ProductFiltersDialog
              filters={advancedFilters}
              onApply={applyAdvancedFilters}
              showModeration={supportsModeration}
            />

            <button
              type="button"
              onClick={clearFilters}
              disabled={activeFilterCount === 0 && urlQuery === ""}
              className="flex h-9 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-muted-foreground ring-offset-background transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </button>
          </div>
        </div>

        {selectedCategory ? (
          <div className="flex flex-wrap items-center gap-2 px-1.5 pb-1.5 pt-0.5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900">
              Category: {localizedText(selectedCategory.name)}
              <button
                type="button"
                aria-label={`Clear category filter ${localizedText(selectedCategory.name)}`}
                onClick={() => updateParams({ category: null }, { resetPage: true })}
                className="rounded-full p-0.5 hover:bg-amber-200/70"
              >
                <X className="size-3" aria-hidden />
              </button>
            </span>
            {categoryQuery?.truncated ? (
              <span className="text-xs text-muted-foreground">
                Branch too large to filter in full — showing products assigned
                directly to this category only.
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {catalogueId && <ImportProgressBanner catalogueId={catalogueId} />}

      <Card>
        <CardContent className="p-0">
          <div className="relative w-full overflow-auto">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[44px]">
                    <Checkbox
                      checked={
                        selectedProducts.length === products.length &&
                        products.length > 0
                      }
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="w-[80px] text-right" />
                  <TableHead className="w-[100px]">ID</TableHead>
                   <TableHead className="min-w-[160px]">Product</TableHead>
                    <TableHead className="w-[120px]">Brand</TableHead>
                    <TableHead className="w-[140px]">Category</TableHead>
                    <TableHead className="w-[80px] text-center">Variants</TableHead>
                    <TableHead className="w-[80px] text-center">Offers</TableHead>
                    <TableHead className="w-[100px] text-right">Price</TableHead>
                    <TableHead className="w-[100px] text-right">Stock</TableHead>
                    <TableHead className="w-[100px] text-center">Published</TableHead>
                    <TableHead className="w-[100px] text-right">Status</TableHead>
                    <TableHead className="w-[100px] text-right">External</TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                   {isLoading ? (
                     <TableRow>
                       <TableCell colSpan={13} className="h-24 text-center">
                        <div className="flex justify-center items-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                          <span className="ml-2">Loading products...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={13}
                        className="h-24 text-center text-red-500"
                      >
                      Error loading products: {error.message}
                    </TableCell>
                  </TableRow>
                ) : products.length > 0 ? (
                  products.map((product) => {
                    const ingestionProgress = getIngestionProgress(product);
                    const categoryPaths = productCategoryPaths(product, categoryNodes);
                    const violation = supportsModeration ? violationSummary(product) : null;
                    const titleStillProcessing =
                      supportsEnrichment &&
                      !hasEnrichedTitle(product) &&
                      isIngestionActive(product);
                    return (
                     <TableRow key={product.id} className={product.isPublished ? "" : "bg-gray-50 opacity-75"}>
                      <TableCell className="w-[44px]">
                        <Checkbox
                          checked={selectedProducts.includes(product.id)}
                          onCheckedChange={() =>
                            toggleSelectProduct(product.id)
                          }
                        />
                      </TableCell>
                      <TableCell className="w-[80px] text-right">
                        <div className="relative inline-block h-12 w-12">
                          <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100">
                            <CatalogueImage
                              image={product.images?.[0]}
                              width={96}
                              alt={productDisplayName(product)}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          {violation ? (
                            <span
                              className={cn(
                                "absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full ring-2 ring-white dark:ring-gray-950",
                                violation.level === "high" ? "bg-red-500" : "bg-yellow-400",
                              )}
                              title={`${violation.count} violation${violation.count === 1 ? "" : "s"} — highest: ${SEVERITY_STYLES[violation.level].label} — ${violation.names.join(", ")}`}
                              aria-label={`${SEVERITY_STYLES[violation.level].label} severity moderation violation`}
                            />
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="w-[100px] font-mono text-sm font-medium">
                        <button
                          onClick={() => navigate(`/catalog/${catalogueId}/product/${product.id}?${searchParams.toString()}`)}
                          className="hover:text-pink-600 hover:underline cursor-pointer text-left"
                        >
                          {formatProductId(product.id)}
                        </button>
                      </TableCell>
                        <TableCell className="min-w-[160px] font-medium">
                           <button
                             onClick={() => navigate(`/catalog/${catalogueId}/product/${product.id}?${searchParams.toString()}`)}
                             className="block w-full truncate hover:text-pink-600 hover:underline cursor-pointer text-left"
                           >
                             <ShimmerText
                               text={productDisplayName(product)}
                               active={titleStillProcessing}
                               tooltip="AI is still enriching this product — showing the supplier's original title"
                               className="truncate"
                             />
                           </button>
                         </TableCell>
                         <TableCell className="w-[120px] text-sm text-muted-foreground">
                           <span className="block truncate">{product.brand?.name ?? "-"}</span>
                         </TableCell>
                         <TableCell className="w-[140px] text-sm text-muted-foreground">
                           {categoryPaths.length === 0 ? (
                             <span>-</span>
                           ) : (
                             <div className="flex items-center gap-2">
                               <span className="block truncate" title={categoryPaths[0].path}>
                                 {categoryPaths[0].leaf}
                               </span>
                               {categoryPaths.length > 1 && (
                                 <Popover>
                                   <PopoverTrigger asChild>
                                     <Badge
                                       variant="secondary"
                                       className="text-xs flex-shrink-0 cursor-pointer hover:bg-secondary/80"
                                     >
                                       +{categoryPaths.length - 1}
                                     </Badge>
                                   </PopoverTrigger>
                                   <PopoverContent className="w-80">
                                     <div className="space-y-2">
                                       <p className="font-semibold text-sm text-gray-700 border-b border-gray-200 pb-2">
                                         Assigned categories
                                       </p>
                                       <div className="space-y-1 text-xs">
                                         {categoryPaths.map((category) => (
                                           <p key={category.path} className="text-gray-800">
                                             {category.path}
                                           </p>
                                         ))}
                                       </div>
                                     </div>
                                   </PopoverContent>
                                 </Popover>
                               )}
                             </div>
                           )}
                         </TableCell>
                         <TableCell className="w-[80px] text-center text-sm tabular-nums">
                           {product.variants?.length ?? 0}
                         </TableCell>
                         <TableCell className="w-[80px] text-center text-sm tabular-nums">
                           {product.offers?.length ?? 0}
                         </TableCell>
                         <TableCell className="w-[100px] text-right font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <span>
                               {(() => {
                                 const displayPrice = getDisplayPrice(product.price);
                                 return (
                                   <span className={displayPrice.isFallback ? "text-gray-500" : ""}>
                                     {displayPrice.isFallback && "*"}
                                     €{displayPrice.amount}
                                     {displayPrice.isFallback && ` (${displayPrice.currency})`}
                                   </span>
                                 );
                               })()}
                            </span>
                             {/* Price currencies count chip when multiple currencies exist */}
                             {product.price && Object.keys(product.price).length > 1 && (
                               <Popover>
                                 <PopoverTrigger asChild>
                                   <Badge 
                                     variant="secondary" 
                                     className="text-xs flex-shrink-0 cursor-pointer hover:bg-secondary/80"
                                   >
                                     +{Object.keys(product.price).length - 1}
                                   </Badge>
                                 </PopoverTrigger>
                                 <PopoverContent className="w-80">
                                   <div className="space-y-2">
                                     <p className="font-semibold text-sm text-gray-700 border-b border-gray-200 pb-2">Available Currencies</p>
                                     <div className="grid grid-cols-2 gap-2 text-xs">
                                        {Object.entries(product.price || {}).map(([currency, price]) => (
                                          <div key={currency} className="flex items-start gap-2">
                                            <span className="font-medium text-gray-600 min-w-[30px]">{currency.toUpperCase()}:</span>
                                            <span className="text-gray-800 truncate">{price ? price.toFixed(2) : "N/A"}</span>
                                          </div>
                                        ))}
                                     </div>
                                   </div>
                                 </PopoverContent>
                               </Popover>
                             )}
                          </div>
                          </TableCell>
                         <TableCell className="w-[100px] text-center">
                           <Badge 
                             variant={product.inStock ? "default" : "secondary"}
                             className={product.inStock 
                               ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200 font-medium text-xs px-2 py-1" 
                               : "bg-red-100 text-red-800 border-red-200 hover:bg-red-200 font-medium text-xs px-2 py-1"
                             }
                           >
                             {product.inStock ? 'In Stock' : 'Out of Stock'}
                            </Badge>
                           </TableCell>
                          <TableCell className="w-[100px] text-center">
                            <Badge 
                              variant={product.isPublished ? "default" : "secondary"}
                              className={product.isPublished 
                                ? "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200 font-medium text-xs px-2 py-1" 
                                : "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 font-medium text-xs px-2 py-1"
                              }
                            >
                              {product.isPublished ? 'Published' : 'Draft'}
                            </Badge>
                           </TableCell>
                          <TableCell className="w-[100px] text-right">
                            <IngestionStatusCell progress={ingestionProgress} />
                          </TableCell>
                          <TableCell className="w-[100px] text-right">
                            {product.url ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    onClick={() => window.open(product.url, '_blank')}
                                    className="inline-flex items-center justify-center p-2 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
                                    title="Visit external product page"
                                  >
                                    <ExternalLink className="h-4 w-4 text-gray-600" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-3" side="left">
                                  <div className="text-sm">
                                    <p className="font-medium text-gray-900 mb-1">Visit external product page</p>
                                    <p className="text-gray-500 text-xs truncate max-w-[250px]">{product.url}</p>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </TableCell>
                    </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                     <TableCell colSpan={13} className="h-24 text-center">
                      No products found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination Section */}
      <div className="space-y-4">
        {/* Desktop pagination info and controls */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <div className="text-sm text-muted-foreground">
              Showing <strong>{(currentPage - 1) * pageSize + 1}</strong> to{" "}
              <strong>{Math.min(currentPage * pageSize, totalItems)}</strong> of{" "}
              <strong>{totalItems}</strong> products
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Items per page:
              </span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) =>
                  updateParams({ pageSize: value === "10" ? null : value }, { resetPage: true })
                }
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={pageSize.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Desktop pagination controls */}
          <div className="hidden sm:block">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              disabled={isLoading}
              siblingCount={1}
              showFirstLast={totalPages > 7}
            />
          </div>
        </div>

        {/* Mobile pagination controls */}
        <MobilePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          disabled={isLoading}
        />
      </div>
    </div>
  );
}

/** One inline filter unit: tiny label + ghost-pill select, shared by every
 * control in the toolbar so they read as a single visual family. */
function FilterSelect({
  label,
  value,
  onValueChange,
  options,
  triggerClassName,
}: {
  readonly label: string;
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly triggerClassName?: string;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <span className="whitespace-nowrap text-xs text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          aria-label={label}
          className={cn(
            "h-9 border-0 bg-muted/50 shadow-none",
            triggerClassName,
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
